/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/prefer-literal-enum-member */
import type {IAttributeData, IIntegerRange, IRect, IRectMargins} from '@colibrio/colibrio-reader-framework/colibrio-core-base';
import type {ILocator, ILocatorData, ISimpleLocatorData} from '@colibrio/colibrio-reader-framework/colibrio-core-locator';
import type {
    IPublicationMetadataData,
} from '@colibrio/colibrio-reader-framework/colibrio-core-publication-base';
import type {
    ContentPositionTimelineUnit,
    IActiveRendererChangedEngineEvent, 
    IContentBlock,
    IContentBlockData,
    IContentLocation,
    IContentPositionTimeline,
    IKeyboardEngineEvent,
    IMouseEngineEvent,
    INavigationEndedEngineEvent,
    INavigationIntentEngineEvent,
    INavigationStartedEngineEvent,
    IPageProgressionTimeline,
    IPageProgressionTimelineEngineEvent,
    IPointerEngineEvent,
    IPublicationStyleFontDefaults,
    IPublicationStyleOptions,
    IPublicationStylePalette,
    IPublicationStyleTextAlignmentOptions,
    IReaderViewAnnotationEngineEvent,
    IReaderViewAnnotationMouseEngineEvent,
    IReaderViewAnnotationPointerEngineEvent,
    IReaderViewEngineEvent,
    IReaderViewOptions,
    IRendererOptions,
    IRendererScrollEngineEvent,
    ISelectionChangedEngineEvent, 
    ISwipeNavigationGestureProgressEngineEventData,
    ISyncMediaPlayer,
    ISyncMediaSegmentData,
    ISyncMediaTimeline, 
    ISyncMediaTimelineRangeData,
    ITransformData,
    ITransformRectZoomOptions, 
    ITtsSynthesizer,
    IVisibleContentRectData,
    NavigationCollectionType, 
    SyncMediaRangePlaybackRemoveReason, 
    SyncMediaRangeSeekBehavior
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import {
    ContainerContentBlockType,
    ContentBlockMarkType,
    MediaContentBlockType,
    TextContentBlockType,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';

import type {MediaType} from "@colibrio/colibrio-reader-framework/colibrio-core-io-base";
import type { ISpeechSynthesisVoiceCollection } from '../utils/WebSpeechUtils';
import type { CirrusReaderLandmark } from './CirrusReaderLandmark';
import type z from 'zod';
import { RendererType } from '../../../../app/configuration/types';
import type { IntRange } from '../utils/IntRange';

export enum CirrusReaderNavigationType {
    GOTO = 'CirrusReader_navigationtype_goto',
    TIMELINEPOSITION = 'CirrusReader_navigationtype_timelineposition',
    GOTOSTART = 'CirrusReader_navigationtype_gotostart',
    NEXT = 'CirrusReader_navigationtype_next',
    PREVIOUS = 'CirrusReader_navigationtype_previous',
    EXTERNAL = 'CirrusReader_navigationtype_external'
}


type CirrusReaderRendererNames = z.infer<typeof RendererType>;
export const CirrusReaderRendererNamesMap: Record<CirrusReaderRendererNames, string> = {
    [RendererType.enum.SpreadSwipeRenderer]: 'Swipe - Spread',
    [RendererType.enum.SinglePageSwipeRenderer]: 'Swipe - Single page',
    [RendererType.enum.FlipBookRenderer]: 'Flipbook',
    [RendererType.enum.StackRenderer]: 'Stack',
    [RendererType.enum.SingleDocumentScrollRenderer]: 'Scroll',
    [RendererType.enum.__responsive__]: 'Responsive'
}

export interface ICirrusReaderContentActiveSelectionStateData {
    locatorData: ISimpleLocatorData | null,
    selector: string | undefined,
    isRange: boolean,
    selectionText: string,
    selectionRectData: IVisibleContentRectData[] | undefined,
    isMouseSelection: boolean,
    vanillaUiHasBeenClosedByUser?: boolean
}

/*
* # ICirrusReaderHighlight
*
* This interface defines the data that is used to describe a highlight annotation in the CirrusReader. This data is used
* by for example the highlight annotation layer and the UI.
*
**/
export interface ICirrusReaderHighlightData {
    _id?: string,
    color: string,
    comment: string,
    locator: string,
    selectionText: string,
    publicationId?: string,
    bookSection?: string,
    dateCreated?: number,
    dateUpdated?: number,
}

/*
* # ICirrusReaderBookmark
*
* This interface defines the data that is used to describe a bookmark annotation in the CirrusReader. This data is used
* by for example the bookmark annotation layer and the UI.
*
**/
export interface ICirrusReaderBookmarkData {
    _id?: string,
    locator: string,
    publicationId?: string,
    color?: string,
    text?: string,
    bookSection?: string,
    dateCreated?: number,
}

/*
* # ICirrusReaderNavigationTreeData
*
* This interface describes the entire publication navigation structure for a `CirrusReaderPublication` (and in turn a
* Colibrio Framework IReaderPublication of course).
*
* This data is used for example by the UI to render the Table of Contents, but also when looking up navigation items
* in relation to positions within the document contents.
*
* The reason for the application specific type will be clear when you have a look at the `ICirrusReaderNavigationItem`
* type that has a lot of extra data in comparison to its Colibrio Framework counterpart.
*
* ## RELATED TYPES
*
* - IReaderPublicationNavigation
* - ICirrusReaderNavigationItem
*
**/
export interface ICirrusReaderNavigationTreeData {
    collections: ICirrusReaderNavigationCollection[],
    items: ICirrusReaderNavigationItem[]
}

/*
* # ICirrusReaderNavigationCollection
*
* This interface describes one type of navigation collection. The types of collections are the same as the once defined
* in the Colibrio Framework `NavigationCollectionType` enum, such as `TOC`, `PAGE_LIST`, `LIST_OF_ILLUSTRATIONS` etc.
*
* ## RELATED TYPES
*
* - NavigationCollectionType
* - ICirrusReaderNavigationItem
*
**/
export interface ICirrusReaderNavigationCollection {
    type: NavigationCollectionType,
    children: ICirrusReaderNavigationItem[]
}

/*
* # ICirrusReaderNavigationItem
*
* This interface describes a more contextually rich representation of a navigation item that what you get from its
* Colibrio Framework counterpart `IReaderPublicationNavigationItem`. On this type you for example get access to timeline
* position and content location data.
*
* Because of the extra data this item is more expensive to build and therefore will benefit from being reused between
* sessions.
*
* ## RELATED TYPES
*
* - IReaderPublicationNavigationItem
* - IReaderPublicationNavigation
* - ICirrusReaderNavigationItem
*
**/
export interface ICirrusReaderNavigationItem {
    title: string,
    collectionType?: NavigationCollectionType,
    locator?: string,
    timelineStartPosition?: number,
    children: ICirrusReaderNavigationItem[]
}

/*
* # ICirrusReaderSyncMediaOptions
*
* This interface describes options for the sync media playback.
*
* ## RELATED TYPES
*
* - ICirrusReaderAppUiSettingsData
* - ICirrusReaderPublicationData
*
**/
export interface ICirrusReaderSyncMediaOptions {
    highlightColor?: string,
    volume?: number,
    rate?: number,
    rangePlaybackOptions?: ICirrusReaderSyncMediaRangePlaybackOptions,
    webSpeechTtsSynthesizer?: ICirrusTtsServiceProvider
}

/*
* # ICirrusReaderSyncMediaRangePlaybackOptions
*
* This interface describes options specific to range playback.
*
* ## RELATED TYPES
*
**/
export interface ICirrusReaderSyncMediaRangePlaybackOptions {
    rangeHighlightColor?: string,
    rangeHighlightVisible?: boolean,
    pauseOnRangeCanceled?: boolean;
    removeRangeOnEndReached?: boolean;
    seekBehaviour?: SyncMediaRangeSeekBehavior;
}

export interface ICirrusReaderShelfItemData extends ICirrusReaderPublicationData {
    latestReadingPositionData?: ICirrusReaderContentPositionData,
    starred?: boolean,
    categories?: string[],
    hasOfflineData?: boolean
}

/*
* # ICirrusReaderPublicationData
*
* This interface describes the complete serializable application state that is related to the CirrusReaderPublication.
* Its purpose is primarily to describe the storage model for the publication and care should be taken when changing
* this type in order to not break user data.
*
* This data is also sent back to the UI once the publication is loaded as metadata.
*
* This data is created by the `CirrusReaderPublication` class.
*
* ## RELATED TYPES
*
* - CirrusReaderPublication
* - ICirrusReaderAppState
* - ICirrusReaderDataStore
*
**/

export interface ICirrusReaderPublicationData {
    id: string,
    fileSourceUri: string,
    format: string,
    url: string,
    title?: string,
    isRtl?: boolean,
    isFixedLayout?: boolean,
    isScripted?: boolean,
    isAudiobook?: boolean,
    hasMoSyncMedia?: boolean,
    metadata?: IPublicationMetadataData,
    coverImageAsBase64?: string,
    language?: string,
}


/*
* # ICirrusReaderPublicationOptionsData
*
* */
export interface ICirrusReaderPublicationOptionsData {
    _id?: string,
    publicationId: string,
    styleOptions?: ICirrusReaderPublicationStyleOptions,
    ttsOptions?: ICirrusReaderSyncMediaOptions
}


export interface ICirrusReaderPublicationStyleOptions extends IPublicationStyleOptions {
    defaultFontFamily?: 'serif' | 'sans-serif' | 'monospace' | 'cursive' | 'fantasy';
    fontSet?: ICirrusReaderFontSet | null;
    fontSizeScaleFactor?: number;
    lineHeightScaleFactor?: number;
    pageMargins?: IRectMargins;
    palette?: ICirrusReaderPublicationStylePalette | null;
    textAlignment?: IPublicationStyleTextAlignmentOptions | null;
    disableCssAnimations?: boolean;
    removePublicationDefinedHorizontalPageMargins?: boolean,
    resetPublicationDefinedTypography?: boolean,
}

export interface ICirrusReaderCombinedTheme extends ICirrusReaderUiColorTheme, ICirrusReaderPublicationStylePalette  {

}

export interface ICirrusReaderUiColorTheme {
    isDark?: boolean;
    accent: string;
    highlightColor: string
    //uiOutlineDark: string;
    //uiOutlineLight: string;
    uiBackgroundDark: string;
    uiBackgroundLight: string;
    uiForegroundDark: string;
    uiForegroundLight: string;
}

export interface ICirrusReaderPublicationStylePalette extends IPublicationStylePalette {
    name: string;

    isDark?: boolean;

    /**
     * The new foreground color for heavily saturated original foreground colors.
     */
    accent: string;

    /**
     * The background color value to use for the darkest original background color.
     */
    backgroundDark: string;

    /**
     * The background color value to use for the brightest original background color.
     */
    backgroundLight: string;

    /**
     * The foreground color value to use for the darkest original foreground color.
     */
    foregroundDark: string;

    /**
     * The foreground color value to use for the brightest original foreground color.
     */
    foregroundLight: string;

    /**
     * The highlight color
     */
    highlightColor: string
}

export interface ICirrusReaderFontFace {
    family: string;
    fontStretch?: string;
    fontStyle?: string;
    fontWeight?: string;
    mediaType: MediaType;
}

export interface ICirrusReaderFontSet {
    name: string;
    defaults: IPublicationStyleFontDefaults;
    fontFaces: ICirrusReaderFontFace[];
    selectors?: {
        family: string;
        selector: string;
    }[],
}

export interface ICirrusReaderFontSetAsset extends ICirrusReaderFontSet {
    fontFaces: ICirrusReaderFontFaceAsset[],
}

export interface ICirrusReaderFontFaceAsset extends ICirrusReaderFontFace {
    src: string;
    srcUrl?: string;
}

/*
* # ICirrusReaderAppState
*
* This interface describes the complete serializable application state including the `ICirrusReaderSyncMediaOptions` and
* the `ICirrusReaderViewOptions`. This is all the data that is needed to restore a previous session.
*
* This data is created by the `CirrusReader` class.
*
* ## RELATED TYPES
*
* - ICirrusReaderViewOptions
*
**/
export interface ICirrusReaderOptionsData {
    _id?: number,
    dateCreated?: number,
    viewOptions?: ICirrusReaderViewOptions,
    syncMediaOptions?: ICirrusReaderSyncMediaOptions
}

/*
* # ICirrusReaderViewOptions
*
* This interface describes the all the serializable application state related to rendering specific features, such as
* what IRenderer, what options to use for that renderer instance etc.
*
* This data is created by the `CirrusReader` class.
*
* ## RELATED TYPES
*
* - ICirrusReaderAppState
* - IRendererOptions
* - IReaderViewOptions
*
**/
export interface ICirrusReaderViewOptions {
    _id?: number,
    colibrioRendererOptions?: IRendererOptions,
    colibrioReaderViewOptions?: IReaderViewOptions,
    activeRendererTypeName?: CirrusReaderRendererNames,
    activeColorTheme?: ICirrusReaderUiColorTheme,
    ignoreAspectRatio?: boolean,
    useReducedMotion?: boolean,
    showPageRangesLayer?: boolean,
    scrollOverflowingTables?: boolean,
}

/*
* # ICirrusReaderInitialOptions
*
* This interface describes data that can be used to not only restore a previous application session using saved
* `ICirrusReaderAppState` state, but also take in possible additional parameters. `initialUrl` for example tells
* the CirrusReader to load the specified url as it boots up. `slowNetwork` is also a helpful parameter that the
* application start up code (index.ts) can send to the CirrusReader so that it may optimise user experience accordingly.
*
* This data is created in the `index.ts` file.
*
* ## RELATED TYPES
*
* - ICirrusReaderAppState
*
**/
export interface ICirrusReaderInitialOptions {
    slowNetwork?: boolean;
    storeOnDevice?: boolean;
    viewOptions?: ICirrusReaderViewOptions;
    syncMediaOptions?: ICirrusReaderSyncMediaOptions;
    customReaderViewContentOnLoading?: string;
    customReaderViewContentOnLoadError?: string;
}

/*
* # ICirrusReaderVisibleRangeData
*
* This interface describes data for the currently visible content. It is sent as event data in the `ICirrusReaderAppEvent_VISIBLE_RANGE_CHANGED`
* app event.
*
* ## RELATED TYPES
*
* - `ICirrusReaderAppEvent_VISIBLE_RANGE_CHANGED`
*
**/
export interface ICirrusReaderVisibleRangeData {
    locator?: ILocator,
    timelinePosition?: number | undefined,
    timelineRange?: IIntegerRange | undefined,
    readingProgression?: number | undefined,
    visibleTextContent?: string,
    bookmarks: ICirrusReaderBookmarkData[],
    highlights: ICirrusReaderHighlightData[],
    navItems: ICirrusReaderNavigationItem[],
    documentIsAtStart: boolean,
    documentIsAtEnd: boolean,
    publicationIsAtStart: boolean,
    publicationIsAtEnd: boolean,
    visibleContentRects?: IVisibleContentRectData[],

}

/*
* # ICirrusReaderReadingPositionData
*
**/
export interface ICirrusReaderContentPositionData {
    _id?: string | undefined,
    publicationId?: string | undefined,
    contentBlockData?: IContentBlockData,
    contentRects?: IVisibleContentRectData[],
    locator: string | undefined,
    navItemData?: ICirrusReaderNavigationItem[],
    documentIndex?: number,
    syntheticPageData?: ICirrusReaderSyntheticPageData[],
    timestamp: number,
}

/*
* # CirrusReaderPublicationLandmarkTypeNote
*
**/

export enum CirrusReaderPublicationLandmarkTypeNote {
    FOOTNOTE = 'FOOTNOTE',
    REARNOTE = 'REARNOTE',
    ENDNOTE = 'ENDNOTE'
}

/*
* # CirrusReaderPublicationLandmarkType
*
**/

export enum CirrusReaderPublicationLandmarkType {
    ASIDE = ContainerContentBlockType.ASIDE,
    ARTICLE = ContainerContentBlockType.ARTICLE,
    FIGURE = ContainerContentBlockType.FIGURE,
    LINK = ContentBlockMarkType.ANCHOR,
    TABLE = ContainerContentBlockType.TABLE,
    SECTION = ContainerContentBlockType.SECTION,
    DESCRIPTION_LIST = ContainerContentBlockType.DESCRIPTION_LIST,
    ORDERED_LIST = ContainerContentBlockType.ORDERED_LIST,
    UNORDERED_LIST = ContainerContentBlockType.UNORDERED_LIST,
    IMAGE = MediaContentBlockType.IMAGE,
    AUDIO = MediaContentBlockType.AUDIO,
    VIDEO = MediaContentBlockType.VIDEO,
    HEADING1 = TextContentBlockType.HEADING1,
    HEADING2 = TextContentBlockType.HEADING2,
    HEADING3 = TextContentBlockType.HEADING3,
    HEADING4 = TextContentBlockType.HEADING4,
    HEADING5 = TextContentBlockType.HEADING5,
    HEADING6 = TextContentBlockType.HEADING6,
    FOOTNOTE = CirrusReaderPublicationLandmarkTypeNote.FOOTNOTE,
    REARNOTE = CirrusReaderPublicationLandmarkTypeNote.REARNOTE,
    ENDNOTE = CirrusReaderPublicationLandmarkTypeNote.ENDNOTE,
}

/*
* # ICirrusReaderPublicationLandmarkData
*
**/
export interface ICirrusReaderPublicationLandmarkData {
    _id?: number,
    publicationId: string,
    documents: ICirrusReaderDocumentLandmarkData[],
}


export interface ICirrusReaderPublicationLandmarkCollectionData {
    headings?: ICirrusReaderLandmarkData[],
    tables?: ICirrusReaderLandmarkData[],
    figures?: ICirrusReaderLandmarkData[],
    audio?: ICirrusReaderLandmarkData[],
    video?: ICirrusReaderLandmarkData[],
    lists?: ICirrusReaderLandmarkData[],
    sections?: ICirrusReaderLandmarkData[],
    articles?: ICirrusReaderLandmarkData[],
    asides?: ICirrusReaderLandmarkData[],
    footnotes?: ICirrusReaderLandmarkData[],
}

/*
* # ICirrusReaderLandmark
*
**/

export interface ICirrusReaderDocumentLandmarkCollection {
    documentIndex: number,
    headings?: CirrusReaderLandmark[],
    tables?: CirrusReaderLandmark[],
    figures?: CirrusReaderLandmark[],
    audio?: CirrusReaderLandmark[],
    video?: CirrusReaderLandmark[],
    lists?: CirrusReaderLandmark[],
    sections?: CirrusReaderLandmark[],
    articles?: CirrusReaderLandmark[],
    asides?: CirrusReaderLandmark[],
    footnotes?: CirrusReaderLandmark[],
    links?: CirrusReaderLandmark[],
}

export interface ICirrusReaderDocumentLandmarkDataCollection {
    documentIndex: number,
    headings?: ICirrusReaderLandmarkData[],
    tables?: ICirrusReaderLandmarkData[],
    figures?: ICirrusReaderLandmarkData[],
    audio?: ICirrusReaderLandmarkData[],
    video?: ICirrusReaderLandmarkData[],
    lists?: ICirrusReaderLandmarkData[],
    sections?: ICirrusReaderLandmarkData[],
    articles?: ICirrusReaderLandmarkData[],
    asides?: ICirrusReaderLandmarkData[],
    footnotes?: ICirrusReaderLandmarkData[],
    links?: ICirrusReaderLandmarkData[],
}

export interface ICirrusReaderPublicationLandmark {
    id?: number,
    publicationId: string,
    documents: ICirrusReaderDocumentLandmarkCollection[],
}

export interface ICirrusReaderPublicationLandmarkData {
    id?: number,
    publicationId: string,
    documents: ICirrusReaderDocumentLandmarkDataCollection[],
}

export interface ICirrusReaderPublicationLandmarkCollection {
    headings?: CirrusReaderLandmark[],
    tables?: CirrusReaderLandmark[],
    figures?: CirrusReaderLandmark[],
    audio?: CirrusReaderLandmark[],
    video?: CirrusReaderLandmark[],
    lists?: CirrusReaderLandmark[],
    sections?: CirrusReaderLandmark[],
    articles?: CirrusReaderLandmark[],
    asides?: CirrusReaderLandmark[],
    footnotes?: CirrusReaderLandmark[],
}

export interface ICirrusReaderLandmarkDataCollection {
    headings?: ICirrusReaderLandmarkData[],
    tables?: ICirrusReaderLandmarkData[],
    figures?: ICirrusReaderLandmarkData[],
    audio?: ICirrusReaderLandmarkData[],
    video?: ICirrusReaderLandmarkData[],
    lists?: ICirrusReaderLandmarkData[],
    sections?: ICirrusReaderLandmarkData[],
    articles?: ICirrusReaderLandmarkData[],
    asides?: ICirrusReaderLandmarkData[],
    footnotes?: ICirrusReaderLandmarkData[],
}


/*
* # ICirrusReaderDocumentLandmarkData
*
**/

export interface ICirrusReaderDocumentLandmarkData {
    documentIndex: number,
    headings?: ICirrusReaderLandmarkData[],
    tables?: ICirrusReaderLandmarkData[],
    figures?: ICirrusReaderLandmarkData[],
    audio?: ICirrusReaderLandmarkData[],
    video?: ICirrusReaderLandmarkData[],
    lists?: ICirrusReaderLandmarkData[],
    sections?: ICirrusReaderLandmarkData[],
    articles?: ICirrusReaderLandmarkData[],
    asides?: ICirrusReaderLandmarkData[],
    footnotes?: ICirrusReaderLandmarkData[],
    links?: ICirrusReaderLandmarkData[],
}

export interface ICirrusReaderLandmarkData extends IContentBlockData {
    timelinePosition: number | undefined;
    subType?: CirrusReaderPublicationLandmarkType;
    description?: string;
    locatorUrl?: string;
    navigationItem?: ICirrusReaderNavigationItem;
    documentIndex?: number;
}

/*
* # ICirrusSyncMediaPlaybackStateData
*
* This interface describes the runtime state of the `ISyncMediaTimeline` during playback. It is sent in various app
* events such as `ICirrusReaderAppEvent_MEDIAPLAYER_PLAYBACK_STATE_CHANGED` and `ICirrusReaderAppEvent_MEDIAPLAYER_PLAYBACK_POSITION_CHANGED`.
*
* ## RELATED TYPES
*
* - CirrusReader
* - CirrusReaderAudiobookPlayer
* - CirrusReaderMediaPlayer
*
* */
export interface ICirrusSyncMediaPlaybackStateData {
    position: number,
    type?: string,
    positionWithinSegment?: number,
    isSeeking?: boolean,
    isPaused?: boolean,
    isMuted?: boolean,
    rate?: number,
    volume?: number,
    navigationItems?: ICirrusReaderNavigationItem[],
    locator?: string,
    segmentIndex?: number,
    segmentData?: ICirrusSyncMediaSegmentData,
    publicationDurationMs?: number,
    playbackRangeData?: ICirrusReaderPlaybackRangeData | undefined,
    text?: string
}

export interface ICirrusReaderPlaybackRangeData {
    // isCanceled?: boolean,
    // isAtEnd?: boolean,
    // cancellationReason?: SyncMediaRangePlaybackCancelReason,
    id: number,
    range: ISyncMediaTimelineRangeData,
    options?: ICirrusReaderSyncMediaRangePlaybackOptions,
}

export interface ICirrusSyncMediaSegmentData extends ISyncMediaSegmentData {
    segmentTimelineStartOffsetMs?: number
}

/*
* # ICirrusReaderReadingProgressionTimeline
*
* A common interface to describe the reading progression in a reader publication using a numeric index.
*
* It is used to hide the differences between the `IContentPositionTimeline` in text based book and the `ISyncMediaTimeline`
* used in audiobooks.
*
* This is a convenience abstraction that is very implementation specific to the CirrusReader. Your own implementation
* may choose another strategy.
*
* ## RELATED TYPES
*
* - CirrusReaderAudiobookProgressionTimeline
* - CirrusReaderProgressionTimeline
* - IContentPositionTimeline
* - ISyncMediaTimeline
*
**/
export interface ICirrusReaderReadingProgressionTimeline {
    create?: (initialState: string | undefined, buildProgressCallback: CirrusProgressEventCallback) => Promise<void>,
    createAudioTimeline?: (colibrioSyncMediaPlayer: ISyncMediaPlayer) => Promise<void>,
    fetchTimelinePosition: (location: ILocator) => Promise<number | undefined>;
    fetchContentLocation: (timelinePosition: number) => Promise<IContentLocation | undefined>;
    fetchContentLocationAsRange: (start: number, end: number) => Promise<IContentLocation | undefined>;
    fetchTimelineRange: (location: ILocator) => Promise<IIntegerRange | undefined>;
    getLength: () => number;
    isReady: () => boolean;
    getBackingTimelineImplementation: () => IContentPositionTimeline | IPageProgressionTimeline | ISyncMediaTimeline;
    toSerializedData: () => string | undefined;
    updateTimelinePosition: (locator: ILocator) => void;
    onTimelineReady: (callback: CirrusTimelineReadyEventCallback) => void;
    serialize: () => Promise<ICirrusReaderReadingProgressionTimelineData | undefined>;
}

export interface ICirrusReaderReadingProgressionTimelineData {
    length: number,
    unit: ContentPositionTimelineUnit | undefined,
    sections?: ICirrusReaderReadingProgressionTimelinePositionData[],
}

export interface ICirrusReaderReadingProgressionTimelinePositionData {
    range: IIntegerRange,
    contentPositionData: ICirrusReaderContentPositionData
}

export interface ICirrusReaderEscapableContentBlockContext {
    blockId: number,
    block: IContentBlock,
    blockRangeLocation: IContentLocation
}

export interface ICirrusReaderEscapableContentBlockDataContext {
    blockId: number,
    blockData: IContentBlockData,
    blockStartLocator: ILocatorData
    blockEndLocator: ILocatorData
}


export interface ICirrusReaderUserProfileData {
    id: string;
    token: string;
}

export enum CirrusReaderPaginationType {
    SECTIONS = 'sections',
    PAGE_LIST = 'pagelist',
    SYNTHETIC = 'synthetic',
    ESTIMATED = 'estimated'
}

export interface ICirrusReaderSyntheticPageData {
    label: string;
    index: number;
    range: IntRange;
    section: IntRange | undefined;
    locatorData?: ILocatorData | undefined;
    navItems?: ICirrusReaderNavigationItem[] | undefined;
}

export interface ICirrusReaderSyntheticPageListData {
    pages: ICirrusReaderSyntheticPageData[];
    paginationType: CirrusReaderPaginationType;
    syntheticPaginationSegmentationMethod: CirrusReaderSyntheticPaginationSegmentationMethod | undefined;
}

export enum CirrusReaderSyntheticPaginationSegmentationMethod {
    // All these codepoints are the ones in Unicode General_Category=Z (Zs | Zl | Zp) except FEFF
    UNICODE_WHITESPACE_SEGMENTATION = 'unicode_whitespace_segmentation',
    // https://unicode.org/reports/tr29/#Table_Word_Break_Property_Values
    UNICODE_DEFAULT_WORD_BREAK_SEGMENTATION = 'unicode_default_word_break_segmentation'
}

export interface ICirrusReaderNavigationAction {
    navigationType: CirrusReaderNavigationType,
    externalNavigation?: boolean,
    locator?: string,
    position?: number,
    url?: string,
    focusNearContentLocation?: boolean
}

export interface ICirrusReaderNavigationOptions {
    scrollRendererScrollBehavior: CirrusScrollRendererGotoScrollBehavior,
    setReadingPositionToVisibleRangeStart?: boolean;
}


export enum CirrusScrollRendererGotoScrollBehavior {
    /**
     * The renderer will not scroll if the location is inside the reading area.
     * See `IVerticalScrollRendererOptions` for how the reading area is defined.
     */
    SCROLL_IF_OUTSIDE_READING_AREA = "SCROLL_IF_OUTSIDE_READING_AREA",

    /**
     * The renderer will not scroll if the location is inside the visible range.
     */
    SCROLL_IF_OUTSIDE_VISIBLE_RANGE = "SCROLL_IF_OUTSIDE_VISIBLE_RANGE",

    /**
     * Always scroll so that the target location is at the top of the reading area.
     * See `IVerticalScrollRendererOptions` for how the reading area is defined.
     */
    ALWAYS_SCROLL = "ALWAYS_SCROLL"
}

export interface ICirrusReaderTransformData {
    transformData: ITransformData | undefined;
    visiblePageClientRects: IRect[];
    zoomTargetClientRect: IRect | undefined;
    publicationViewportScale: number;
}

export interface ICirrusReaderGuidedReadingReadingOptions {
    defaultTransitionOptions: ITransformRectZoomOptions;
    maskPageContent: boolean;
}

export interface ICirrusReaderGuidedReadingTimelineData {
    publicationId: string;
    sequences: ICirrusReaderGuidedReadingSequenceData[];
}
export interface ICirrusReaderGuidedReadingSequenceData {
    locator: string;
    steps: ICirrusReaderGuidedReadingStepData[];
}
export interface ICirrusReaderGuidedReadingStepData {
    rect: IRect;
    locator: string;
    transitionOptions?: ITransformRectZoomOptions;
}

export interface ICirrusReaderGuidedReadingReadingPositionData {
    publicationId: string;
    sequence: ICirrusReaderGuidedReadingSequenceData;
    step: ICirrusReaderGuidedReadingStepData;
    time: number
}

export interface ICirrusTtsServiceProvider extends ITtsSynthesizer, EventListenerObject {
    supportsWordBoundaries: boolean;
    setActiveVoice(voice: SpeechSynthesisVoice | undefined): void;
    getActiveVoice(): SpeechSynthesisVoice | undefined;
    fetchVoices(): Promise<ISpeechSynthesisVoiceCollection | undefined>;
}

export interface IHttpHeadResponse {
    mediaType: string | undefined,
    contentLength: number;
    contentType: string | null;
}

export interface ICirrusReaderSignature {
    p: string,
    k: string,
    s: string,
    tokens: {
        rst: string,
        refresh: string
    }
}

export interface IPublicationSearchIndexContentNodeData {
    tocTitle: string | undefined,
    // The source ContentBlock id
    id: number,
    // The locator to the source ContentBlock
    locator: string,
    // The character offsets within the parent document node. Use this data to extract the text content from the indexed
    // text contents of the document.
    charRangeInIndexedDocumentText?: IntRange | undefined,
    type: ContainerContentBlockType | MediaContentBlockType | TextContentBlockType,
    attributes: IAttributeData[],
    documentIndex: number
}

export interface ICirrusReaderSearchResultNodes {
    locator: string,
    startOffsetInIndexedNodeText: number,
    endOffsetInIndexedNodeText: number,
    indexedNodeText?: string,
    searchIndexNode: IPublicationSearchIndexContentNodeData
}
export interface ICirrusReaderPublicationSearchResultItem {
    bookSection: string | undefined,
    nodes: ICirrusReaderSearchResultNodes[],
    searchTermStartOffsetInIndexedContent: number,
    searchTermEndOffsetInIndexedContent: number,
    locator: string,
    intersectingNodeText?: string,
    readerDocumentIndexInSpine?: number,
}

// CALLBACK TYPES

export type CirrusNavigationStartedEventCallback = (ev: INavigationStartedEngineEvent) => void;
export type CirrusNavigationEndedEventCallback = (ev: INavigationEndedEngineEvent) => void;
export type CirrusReaderViewEventCallback = (ev: IReaderViewEngineEvent) => void;
export type CirrusReaderViewScrollEventCallback = (ev:  IRendererScrollEngineEvent) => void;
export type CirrusReaderViewReadingAreaChangedEventCallback = (ev:  IReaderViewEngineEvent) => void;
export type CirrusPageProgressionTimelineRecalculatedCallback = (ev: IPageProgressionTimelineEngineEvent) => void;
export type CirrusNavigationIntentEventCallback = (ev: INavigationIntentEngineEvent) => void;
export type CirrusBookmarkReadingPositionIntentEventCallback = () => void;
export type CirrusSelectionChangedEventCallback = (ev: ISelectionChangedEngineEvent, selectionRectData?: IVisibleContentRectData[] | undefined) => void;
export type CirrusPointerEngineEventCallback = (ev: IPointerEngineEvent, contentPositionData?: ICirrusReaderContentPositionData) => void;
export type CirrusMouseEngineEventCallback = (ev: IMouseEngineEvent) => void;
export type CirrusMouseEventCallback = (ev: MouseEvent) => void;
export type CirrusPointerEventCallback = (ev: PointerEvent) => void;
export type CirrusViewTransformChangedCallback = (transformState: ITransformData | null) => void;
export type CirrusZoomToolDragCallback = (ev: PointerEvent, rect?: DOMRect) => void;
export type CirrusActiveRendererChangedEventCallback = (ev: IActiveRendererChangedEngineEvent) => void;
export type CirrusKeyboardEventCallback = (ev: IKeyboardEngineEvent) => void;
export type CirrusAnnotationMouseEventCallback = (ev: IReaderViewAnnotationMouseEngineEvent) => void;
export type CirrusAnnotationPointerEventCallback = (ev: IReaderViewAnnotationPointerEngineEvent) => void;
export type CirrusAnnotationEventCallback = (ev: IReaderViewAnnotationEngineEvent) => void;
export type CirrusAnnotationCollectionChangedEventCallback = () => void;
export type CirrusTimelineReadyEventCallback = (timelineData: ICirrusReaderReadingProgressionTimelineData) => void;
export type CirrusProgressEventCallback = (progress: number, resourceId?: string) => void;
export type CirrusHighlightDataCallback = (highlight: ICirrusReaderHighlightData, silent?: boolean) => void;
export type CirrusBookmarkDataCallback = (bookmark: ICirrusReaderBookmarkData, silent?: boolean) => void;
export type CirrusOpenFileCallback = (fileObject: File) => void;
export type CirrusOpenUrlCallback = (url: string) => void;
export type CirrusVoidCallback = () => void;
export type CirrusReaderViewOptionsCallback = (options: ICirrusReaderViewOptions) => void;
export type CirrusPublicationDataCallback = (publicationData: ICirrusReaderPublicationData) => void;
export type CirrusPublicationOptionsDataCallback = (publicationOptionsData: ICirrusReaderPublicationOptionsData) => void;
export type CirrusStringValueCallback = (value: string) => void;
export type CirrusNumberValueCallback = (value: number) => void;
export type CirrusBooleanValueCallback = (value: boolean) => void;
export type CirrusMenuHiddenCallback = (menuName: string, silent: boolean) => void;
export type CirrusAppStateUpdatedCallback = (appState: ICirrusReaderOptionsData) => void;
export type CirrusMediaPlayerPlaybackStateChangedCallback = (state: ICirrusSyncMediaPlaybackStateData) => void;
export type CirrusMediaPlayerPlaybackRangeRemovedCallback = (state: ICirrusSyncMediaPlaybackStateData, reason?: SyncMediaRangePlaybackRemoveReason) => void;
export type CirrusZoomToLevelCallback = (zoomLevel: number, animate?: boolean) => void;
export type CirrusZoomToRectCallback = (zoomRect: IRect, transformOptions: ITransformRectZoomOptions) => void;
export type CirrusReadingRulerDragCallback = (ev: PointerEvent, intersectingRectData: IVisibleContentRectData) => void;
export type CirrusReadingRulerMoveCallback = (intersectingRectData: IVisibleContentRectData, affectReadingPosition?: boolean) => void;
export type CirrusContentPositionCallback = (contentPositionData: ICirrusReaderContentPositionData) => void;
export type CirrusSwipeNavigationProgressCallback = (eventData: ISwipeNavigationGestureProgressEngineEventData) => void;
export type CirrusMediaPlayerEscapabilityContextCallback = (context: ICirrusReaderEscapableContentBlockContext[]) => void;